package com.essntl.features.service.ui.components

import androidx.compose.foundation.layout.*
import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.essntl.features.area.domain.model.AreaModel
import com.essntl.features.category.domain.model.CategoryModel
import com.essntl.features.service.domain.model.service.ServiceFilterOptions
import com.essntl.features.service.domain.model.service.ServiceOrderType
import com.essntl.features.service.ui.rememberSortedTagList
import com.essntl.features.supplier.ui.allsuppliers.AllSuppliersEvent
import com.essntl.features.supplier.ui.allsuppliers.AllSuppliersState
import com.essntl.features.tag.domain.model.TagModel
import com.essntl.features.tag.ui.TagDropDownMenu
import com.essntl.uikit.components.button.flat.EsSmallFlatButton
import com.essntl.uikit.components.button.outline.EsMediumOutlineButton
import com.essntl.uikit.components.dropdown.EsFilterDropDownItem
import com.essntl.uikit.components.dropdown.EsFilterDropDownMenu
import com.essntl.uikit.components.dropdown.EsInputDropdownMenu
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsMediumIcon
import com.essntl.uikit.components.input.EsInput
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.rememberContains
import com.essntl.uikit.rememberEquals
import kotlinx.coroutines.delay

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun ServiceFilterSection(
    categoryList: List<CategoryModel>,
    tagList: List<TagModel>,
    areaList: List<AreaModel>,
    filterOptions: ServiceFilterOptions,
    onFilterOptionsChange: (ServiceFilterOptions) -> Unit,
    isCategoryLoading: Boolean = false,
    isTagLoading: Boolean = false,
    isAreaLoading: Boolean = false,
    isCategoryFilterEnabled: Boolean = true,
    isTagFilterEnabled: Boolean = true,
    isAreaFilterEnabled: Boolean = true,
    isSortFilterEnabled: Boolean = true,
    isSupplierFilterEnabled: Boolean = false,
    allSuppliersState: AllSuppliersState? = null,
    onEventAllSupplier: (AllSuppliersEvent) -> Unit = {},
    modifier: Modifier,
) {
    var searchText by rememberSaveable { mutableStateOf(allSuppliersState?.getAllSuppliers?.query ?: "") }

    LaunchedEffect(searchText) {
        if (allSuppliersState != null) {
            delay(500)
            onEventAllSupplier(
                AllSuppliersEvent.EditFilterOptions(
                    allSuppliersState.filterOptions.copy(
                        searchText = searchText,
                    ),
                ),
            )
        }
    }

    FlowRow(
        verticalArrangement = Arrangement.spacedBy(1.spacing),
        horizontalArrangement = Arrangement.spacedBy(1.spacing),
        modifier = modifier,
    ) {
        // Category Filter
        if (isCategoryFilterEnabled) {
            var isFilterByCategoryExpanded by remember { mutableStateOf(false) }

            val selectedCategoryList = rememberContains(
                categoryList,
                filterOptions.selectedCategoryIds,
            ) { category -> category.id }

            Box {
                EsMediumOutlineButton(
                    text = when (selectedCategoryList.size) {
                        0 -> "Category"
                        1 -> selectedCategoryList.first().name
                        else -> "${selectedCategoryList.first().name}, +${selectedCategoryList.size - 1}"
                    },
                    onClick = {
                        isFilterByCategoryExpanded = !isFilterByCategoryExpanded
                    },
                    suffixIconRes = if (isFilterByCategoryExpanded) EsIcons.ArrowUp else EsIcons.ArrowDown,
                )

                EsFilterDropDownMenu(
                    expanded = isFilterByCategoryExpanded,
                    onDismissRequest = { isFilterByCategoryExpanded = false },
                    list = categoryList,
                    selectedItemIds = filterOptions.selectedCategoryIds,
                    onClearAll = {
                        onFilterOptionsChange(
                            filterOptions.copy(
                                selectedCategoryIds = setOf(),
                            ),
                        )

                        if (allSuppliersState != null &&
                            isSupplierFilterEnabled
                        ) onEventAllSupplier(
                            AllSuppliersEvent.EditFilterOptions(
                                allSuppliersState.filterOptions.copy(
                                    selectedCategoryIds = setOf(),
                                ),
                            ),
                        )
                    },
                    loading = isCategoryLoading,
                    content = {
                        categoryList.forEach { category ->
                            val isSelected = rememberEquals(
                                category,
                                filterOptions.selectedCategoryIds,
                            ) { cat -> cat.id }

                            key(category.id) {
                                EsFilterDropDownItem(
                                    onClick = {
                                        onFilterOptionsChange(
                                            filterOptions.copy(
                                                selectedCategoryIds =
                                                    if (category.id in filterOptions.selectedCategoryIds)
                                                        filterOptions.selectedCategoryIds - category.id
                                                    else
                                                        filterOptions.selectedCategoryIds + category.id,
                                            ),
                                        )

                                        if (allSuppliersState != null &&
                                            isSupplierFilterEnabled
                                        ) onEventAllSupplier(
                                            AllSuppliersEvent.EditFilterOptions(
                                                allSuppliersState.filterOptions.copy(
                                                    selectedCategoryIds =
                                                        if (category.id in filterOptions.selectedCategoryIds)
                                                            filterOptions.selectedCategoryIds - category.id
                                                        else
                                                            filterOptions.selectedCategoryIds + category.id,
                                                ),
                                            ),
                                        )
                                    },
                                    title = category.name,
                                    selected = isSelected,
                                    modifier = Modifier
                                        .padding(
                                            vertical = .5f.spacing,
                                            horizontal = 1.5f.spacing,
                                        ),
                                )
                            }
                        }
                    },
                    modifier = Modifier
                        .width(308.dp),
                )
            }
        }

        // Area Filter
        if (isAreaFilterEnabled) {
            var isFilterByAreaExpanded by remember { mutableStateOf(false) }

            val selectedAreaList = rememberContains(
                areaList,
                filterOptions.selectedAreaIds,
            ) { area -> area.id }

            Box {
                EsMediumOutlineButton(
                    text = when (selectedAreaList.size) {
                        0 -> "Area"
                        1 -> "${selectedAreaList.first().name}"
                        else -> "${selectedAreaList.first().name}, +${selectedAreaList.size - 1}"
                    },
                    onClick = {
                        isFilterByAreaExpanded = !isFilterByAreaExpanded
                    },
                    suffixIconRes = if (isFilterByAreaExpanded) EsIcons.ArrowUp else EsIcons.ArrowDown,
                )

                EsFilterDropDownMenu(
                    expanded = isFilterByAreaExpanded,
                    onDismissRequest = { isFilterByAreaExpanded = false },
                    list = areaList,
                    selectedItemIds = filterOptions.selectedAreaIds,
                    onClearAll = {
                        onFilterOptionsChange(
                            filterOptions.copy(
                                selectedAreaIds = setOf(),
                            ),
                        )
                        if (allSuppliersState != null &&
                            isSupplierFilterEnabled
                        ) onEventAllSupplier(
                            AllSuppliersEvent.EditFilterOptions(
                                allSuppliersState.filterOptions.copy(
                                    selectedAreaIds = setOf(),
                                ),
                            ),
                        )
                    },
                    loading = isAreaLoading,
                    content = {
                        areaList.sortedBy { it.name }.forEach { area ->
                            val isSelected = rememberEquals(
                                area,
                                filterOptions.selectedAreaIds,
                            ) { a -> a.id }

                            key(area.id) {
                                EsFilterDropDownItem(
                                    onClick = {
                                        onFilterOptionsChange(
                                            filterOptions.copy(
                                                selectedAreaIds =
                                                    if (area.id in filterOptions.selectedAreaIds)
                                                        filterOptions.selectedAreaIds - area.id
                                                    else
                                                        filterOptions.selectedAreaIds + area.id,
                                            ),
                                        )

                                        if (allSuppliersState != null &&
                                            isSupplierFilterEnabled
                                        ) onEventAllSupplier(
                                            AllSuppliersEvent.EditFilterOptions(
                                                allSuppliersState.filterOptions.copy(
                                                    selectedAreaIds =
                                                        if (area.id in filterOptions.selectedAreaIds)
                                                            filterOptions.selectedAreaIds - area.id
                                                        else
                                                            filterOptions.selectedAreaIds + area.id,
                                                ),
                                            ),
                                        )
                                    },
                                    title = area.name ?: "",
                                    selected = isSelected,
                                    modifier = Modifier
                                        .padding(
                                            vertical = .5f.spacing,
                                            horizontal = 1.5f.spacing,
                                        ),
                                )
                            }
                        }
                    },
                    modifier = Modifier
                        .width(308.dp),
                )
            }
        }

        // Tag Filter
        if (isTagFilterEnabled) {
            var isFilterByTagExpanded by remember { mutableStateOf(false) }

            val selectedTagList = rememberContains(
                tagList,
                filterOptions.selectedTagIds,
            ) { tag -> tag.id }

            val sortedTagList = rememberSortedTagList(
                tagList,
                filterOptions.selectedTagIds,
            )

            Box {
                EsMediumOutlineButton(
                    text = when (selectedTagList.size) {
                        0 -> "Tags"
                        1 -> selectedTagList.first().name
                        else -> "${selectedTagList.first().name}, +${selectedTagList.size - 1}"
                    },
                    onClick = {
                        isFilterByTagExpanded = !isFilterByTagExpanded
                    },
                    suffixIconRes = if (isFilterByTagExpanded) EsIcons.ArrowUp else EsIcons.ArrowDown,
                )

                TagDropDownMenu(
                    expanded = isFilterByTagExpanded,
                    onDismissRequest = { isFilterByTagExpanded = false },
                    tagList = sortedTagList,
                    selectedTagIds = filterOptions.selectedTagIds,
                    onTagToggle = {
                        onFilterOptionsChange(
                            filterOptions.copy(
                                selectedTagIds =
                                    if (it.id in filterOptions.selectedTagIds)
                                        filterOptions.selectedTagIds - it.id
                                    else
                                        filterOptions.selectedTagIds + it.id,
                            ),
                        )
                    },
                    onAddTag = {},
                    addTag = false,
                    loading = isTagLoading,
                    modifier = Modifier
                        .width(308.dp),
                )
            }
        }

        // Sort Filter
        if (isSortFilterEnabled) {
            var isSortFilterExpanded by remember { mutableStateOf(false) }
            val serviceOrderTypes =
                listOf(
                    ServiceOrderType.Alphabetical,
                    ServiceOrderType.LastCreated,
                    ServiceOrderType.RecentlyChanged,
                )

            Box {
                EsMediumOutlineButton(
                    text = filterOptions.selectedServiceOrderType.orderName,
                    onClick = {
                        isSortFilterExpanded = !isSortFilterExpanded
                    },
                    suffixIconRes = EsIcons.Sort,
                )

                EsFilterDropDownMenu(
                    expanded = isSortFilterExpanded,
                    onDismissRequest = { isSortFilterExpanded = false },
                    list = serviceOrderTypes,
                    selectedItemIds = setOf(filterOptions.selectedServiceOrderType),
                    clearing = false,
                    content = {
                        serviceOrderTypes.forEach { order ->
                            val isSelected = rememberEquals(
                                order,
                                setOf(filterOptions.selectedServiceOrderType),
                            ) { it }

                            key(order) {
                                EsFilterDropDownItem(
                                    onClick = {
                                        onFilterOptionsChange(
                                            filterOptions.copy(
                                                selectedServiceOrderType =
                                                    if (order == filterOptions.selectedServiceOrderType)
                                                        ServiceOrderType.Default
                                                    else
                                                        order,
                                            ),
                                        )
                                    },
                                    title = order.orderName,
                                    selected = isSelected,
                                    modifier = Modifier
                                        .padding(
                                            vertical = .5f.spacing,
                                            horizontal = 1.5f.spacing,
                                        ),
                                )
                            }
                        }
                    },
                    modifier = Modifier
                        .width(308.dp),
                )
            }
        }

        // Supplier Filter
        if (isSupplierFilterEnabled &&
            allSuppliersState != null
        ) {
            Box(
                modifier = Modifier.width(200.dp),
            ) {
                EsInputDropdownMenu(
                    items = allSuppliersState.getAllSuppliers.data,
                    selectedItem = filterOptions.supplier,
                    text = { it.prettyName },
                    onItemSelected = {
                        onFilterOptionsChange(
                            filterOptions.copy(
                                supplier = it,
                            ),
                        )
                    },
                    placeholder = "Supplier",
                    loading = allSuppliersState.getAllSuppliers.isLoading &&
                        allSuppliersState.getAllSuppliers.data.isEmpty(),
                    topContent = {
                        Spacer(Modifier.height(0.5.spacing))
                        EsInput(
                            value = searchText,
                            onValueChange = {
                                searchText = it
                            },
                            placeholder = "Search",
                            prefix = {
                                EsMediumIcon(res = EsIcons.Search)
                            },
                            modifier = Modifier.fillMaxWidth(),
                        )

                        Spacer(Modifier.height(1.spacing))
                    },
                    modifier = Modifier.width(200.dp),
                ) {
                    if (!allSuppliersState.getAllSuppliers.isLastPage) {
                        Spacer(modifier = Modifier.height(2.spacing))

                        EsMediumOutlineButton(
                            onClick = {
                                onEventAllSupplier(AllSuppliersEvent.LoadSuppliers())
                            },
                            text = "Load more",
                            loading = allSuppliersState.getAllSuppliers.isLoading,
                            modifier =
                                Modifier
                                    .align(Alignment.CenterHorizontally),
                        )
                    }
                }
            }
        }

        if (filterOptions != ServiceFilterOptions()) {
            EsSmallFlatButton(
                onClick = {
                    onFilterOptionsChange(ServiceFilterOptions())
                    if (allSuppliersState != null &&
                        isSupplierFilterEnabled
                    ) onEventAllSupplier(
                        AllSuppliersEvent.EditFilterOptions(
                            allSuppliersState.filterOptions.copy(
                                selectedAreaIds = setOf(),
                                selectedCategoryIds = setOf(),
                            ),
                        ),
                    )
                },
                text = "Clear all filters",
            )
        }
    }
}